import React, { FC, useCallback, useMemo, useRef } from "react";
import CharactersForm from "@components/CharactersForm/CharactersForm";
import { useGetCharactersQuery } from "@store/characterApi";
import { useDispatch, useSelector } from "react-redux";
import { setMessage } from "@store/snackbarSlice";
import { useTranslation } from "react-i18next";
import Loader from "@components/Loader/Loader";
import { RootState } from "@store/store";
import styled from "@emotion/styled";
import { Box, Button, Grid2 as Grid, IconButton, Typography } from "@mui/material";
import PhotoMetaData, {
  type PhotoMetaDataHandle,
} from "@components/Home/Photos/PhotoMetaData";
import StyledDrawer from "@components/StyledDrawer";
import {
  useDeletePhotoLabelMutation,
  useGetPhotoQuery,
  useUpdatePhotoMutation,
} from "@store/photoApi";
import StyledChip from "@components/StyledChip";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import TextIconButton from "@components/TextIconButton";
import HomeFormButtons from "@components/Home/HomeFormButtons/HomeFormButtons";

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 16px;
`;

const Title = styled.div`
  font-size: 32px;
  line-height: 48px;
  font-weight: 400;
  color: #171a1fff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  flex: 1;
`;

const Image = styled.img`
  width: 100%;
`;

const SectionTitle = styled(Box)`
  font-size: 20px;
  line-height: 30px;
  font-weight: 600;
  color: #565d6dff;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PhotoDrawer: FC<{
  onClose: () => void;
  photoId: string | undefined;
  onDelete: () => void;
  onPrevious: () => void;
  onNext: () => void;
}> = ({ onClose, photoId, onDelete, onPrevious, onNext }) => {
  const { t } = useTranslation();
  const { data: photo } = useGetPhotoQuery(photoId!, { skip: !photoId });
  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const { data: charactersData, isLoading: isLoadingCharacters } =
    useGetCharactersQuery(
      { patientId: selectedPatientId!, photoId: photo?.id },
      { skip: !selectedPatientId && !photo },
    );
  const characters = useMemo(
    () => charactersData?.data || [],
    [charactersData],
  );
  const dispatch = useDispatch();
  const [deletePhotoLabel] = useDeletePhotoLabelMutation();
  const [updatePhoto, { isLoading: isSaving }] = useUpdatePhotoMutation();
  const photoMetaDataRef = useRef<PhotoMetaDataHandle>(null);

  const deleteLabel = useCallback(
    async (labelId: string) => {
      if (!photoId) {
        return;
      }
      const { error } = await deletePhotoLabel({
        photoId,
        labelId,
      });

      if (error) {
        dispatch(
          setMessage({ content: t(`errors.unknown`), severity: "error" }),
        );
      }
    },
    [photoId, deletePhotoLabel, dispatch, t],
  );

  const handleSavePhotoMeta = useCallback(
    async (payload: { placeId?: string; userDescription?: string }) => {
      if (!photoId) return;
      const { error } = await updatePhoto({
        photoId,
        placeId: payload.placeId,
        userDescription: payload.userDescription,
      });
      if (error) {
        dispatch(
          setMessage({
            content: t("errors.unknown"),
            severity: "error",
          }),
        );
      } else {
        dispatch(
          setMessage({
            content: t("photos.photoDrawer.saveSuccessMessage"),
            severity: "success",
          }),
        );
        onClose();
      }
    },
    [photoId, updatePhoto, dispatch, t, onClose],
  );

  return (
    <StyledDrawer
      onClose={onClose}
      open={!!photoId}
      width="900px"
      slots={{
        topRight: (
          <Box display="flex" gap="8px">
            <IconButton onClick={onPrevious}>
              <NavigateBeforeOutlinedIcon />
            </IconButton>
            <IconButton onClick={onNext}>
              <NavigateNextOutlinedIcon />
            </IconButton>
          </Box>
        ),
      }}
    >
      <DrawerContent>
        <Header>
          <Title>{photo?.fileName || ""}</Title>

          <Button variant="outlined" color="error" onClick={onDelete}>
            {t("photos.photoDrawer.deleteButton")}
          </Button>
        </Header>

        <Body>
          {!selectedPatientId ||
          isLoadingCharacters ||
          !photo ||
          photo.id !== photoId ? (
            <Loader />
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Image src={photo.fileUrl} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <PhotoMetaData
                    ref={photoMetaDataRef}
                    photo={photo}
                    isEditMode
                    onSave={handleSavePhotoMeta}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={1}>
                <Grid size={{ xs: 4, sm: 2 }}>
                  <SectionTitle>
                    {t("photos.photoDrawer.labels.title")}
                  </SectionTitle>
                </Grid>
                <Grid size={{ xs: 8, sm: 10 }}>
                  <Box display="flex" gap="12px" flexWrap="wrap">
                    {photo.photoLabels.map((label) => (
                      <StyledChip
                        key={label.id}
                        label={label.label.name}
                        color="primary"
                        onDelete={() => deleteLabel(label.labelId)}
                      />
                    ))}
                  </Box>
                </Grid>
              </Grid>

              <Box>
                <SectionTitle marginBottom="12px">
                  {t("photos.photoDrawer.people.title")}
                </SectionTitle>

                {characters.length ? (
                  <CharactersForm
                    patientId={selectedPatientId}
                    characters={characters}
                  />
                ) : (
                  <Typography fontSize="14px">
                    {t("photos.photoDrawer.noFaces")}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Body>

        {photo && photo.id === photoId && (
          <HomeFormButtons>
            <TextIconButton
              disabled={isSaving}
              variant="contained"
              size="large"
              text={
                isSaving
                  ? t("photos.photoDrawer.edit.saving")
                  : t("photos.photoDrawer.edit.save")
              }
              icon={SaveOutlinedIcon}
              onClick={() => photoMetaDataRef.current?.save()}
            />
          </HomeFormButtons>
        )}
      </DrawerContent>
    </StyledDrawer>
  );
};

export default PhotoDrawer;
